<script setup>
import { computed, onMounted, onUnmounted } from 'vue'

const props = defineProps({
  // Support both v-model (modelValue) and v-model:visible (visible)
  modelValue: {
    type: Boolean,
    default: undefined
  },
  visible: {
    type: Boolean,
    default: undefined
  },
  title: {
    type: String,
    default: ''
  },
  loading: {
    type: Boolean,
    default: false
  },
  size: {
    type: String,
    default: 'md' // sm | md | lg | xl
  },
  zIndex: {
    type: String,
    default: 'z-50'
  }
})

const emit = defineEmits(['update:modelValue', 'update:visible'])

// Resolve which prop is being used
const isVisible = computed(() => {
  if (props.modelValue !== undefined) return props.modelValue
  if (props.visible !== undefined) return props.visible
  return false
})

function close() {
  if (!props.loading) {
    emit('update:modelValue', false)
    emit('update:visible', false)
  }
}

function onKeydown(event) {
  if (event.key === 'Escape' && isVisible.value && !props.loading) {
    close()
  }
}

onMounted(() => {
  document.addEventListener('keydown', onKeydown)
})

onUnmounted(() => {
  document.removeEventListener('keydown', onKeydown)
})
</script>

<template>
  <Teleport to="body">
    <div
      v-if="isVisible"
      class="fixed inset-0 flex items-center justify-center bg-navy/75 backdrop-blur-sm"
      :class="zIndex"
      @click.self="close"
    >
      <div
        class="bg-offwhite rounded-[20px] shadow-xl w-[90vw] flex flex-col max-h-[90vh]"
        :class="{
          'max-w-sm': size === 'sm',
          'max-w-lg': size === 'md',
          'max-w-2xl': size === 'lg',
          'max-w-4xl': size === 'xl'
        }"
      >
        <!-- Header -->
        <div class="bg-navy text-offwhite text-sm font-bold px-6 py-4 rounded-t-[20px] border-b-2 border-teal flex items-center justify-between flex-shrink-0">
          <span>{{ title }}</span>
          <button
            type="button"
            :disabled="loading"
            class="text-offwhite/60 hover:text-offwhite disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            @click="close"
            aria-label="Fermer"
          >
            <i class="pi pi-times text-sm" />
          </button>
        </div>

        <!-- Body -->
        <div class="px-6 py-5 flex-1 overflow-y-auto">
          <slot />
        </div>

        <!-- Footer -->
        <div v-if="$slots.footer" class="bg-offwhite-dim px-6 py-4 rounded-b-[20px] flex justify-end gap-2 flex-shrink-0 border-t border-stone-200">
          <slot name="footer" />
        </div>
      </div>
    </div>
  </Teleport>
</template>
